/* eslint-disable max-lines */
import { Text, Title } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { getSafeAreaPaddings } from "@src/appV2/redesign/utils/getSafeAreaPaddings";
import { useStartAssessment } from "@src/appV2/SkillsAssessment/api/useStartAssessment";
import { useSubmitAssessment } from "@src/appV2/SkillsAssessment/api/useSubmitAssessment";
import {
  type OrientationVideo,
  type SkillsAssessment,
  type SkillsAssessmentStatus,
  SkillsAssessmentWorkerStatus,
} from "@src/appV2/SkillsAssessment/types";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { ClipboardOrientationVideoStartStep } from "./ClipboardOrientationVideoStartStep";
import { QuizStep } from "./QuizStep";
import { RecapStep } from "./RecapStep";
import { VideoStep } from "./VideoStep";

interface OrientationFlowProps {
  orientation: SkillsAssessment;
  orientationStatus: SkillsAssessmentStatus;
  workerId: string;
  orientationVideos: OrientationVideo[];
  qualificationName: string;
  timeToCompleteInMinutes: number;
  onComplete: () => Promise<void>;
  isRefreshingOrientation: boolean;
}

interface OrientationFlowStep {
  step: number;
  stepDetails:
    | {
        type: "intro";
      }
    | {
        type: "video";
        videoUrl: string;
        videoPosterUrl?: string;
      }
    | {
        type: "recap";
        recapText: string[];
        confirmationText: string;
      }
    | {
        type: "quiz";
        quiz: Array<{
          options: Array<{ text: string; isCorrect: boolean }>;
          question: string;
        }>;
      };
}

export function OrientationFlow(props: OrientationFlowProps) {
  const {
    orientationVideos,
    qualificationName,
    timeToCompleteInMinutes,
    workerId,
    onComplete,
    isRefreshingOrientation,
    orientation,
    orientationStatus,
  } = props;

  const { id: assessmentId } = orientation;

  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const { mutateAsync: submitAssessment, isLoading: isSubmittingAssessment } =
    useSubmitAssessment();
  const { mutateAsync: startAssessment, isLoading: isStarting } = useStartAssessment();
  const history = useHistory();

  const steps: OrientationFlowStep[] = useMemo(() => {
    return orientationVideos.reduce<OrientationFlowStep[]>(
      (result, videoData) => {
        return [
          ...result,
          {
            step: result.length,
            stepDetails: {
              type: "video",
              videoUrl: videoData.videoUrl,
              videoPosterUrl: videoData.videoPosterUrl,
            },
          },

          // If there are quiz questions, add a quiz step else add a recap step.
          // This is for backwards compatibility while we migrate to the new quiz questions.
          // We will remove recap step once we start returning quiz questions in the API.
          "quiz" in videoData
            ? {
                step: result.length + 1,
                stepDetails: {
                  type: "quiz",
                  quiz: videoData.quiz,
                },
              }
            : {
                step: result.length + 1,
                stepDetails: {
                  type: "recap",
                  recapText: videoData.recapText,
                  confirmationText: videoData.confirmationText,
                },
              },
        ];
      },
      [
        {
          step: 0,
          stepDetails: { type: "intro" },
        },
      ]
    );
  }, [orientationVideos]);

  useEffect(() => {
    if (orientationVideos.length === 0) {
      logError("No Demo Shift videos found for this qualification", {
        error: new Error("No Demo Shift videos found for this qualification"),
        metadata: {
          assessmentId,
          workerId,
          qualificationName,
        },
      });
    }
  }, [assessmentId, orientationVideos.length, qualificationName, steps, workerId]);

  const onBack = () => {
    if (currentStepIndex === 0) {
      history.goBack();
    } else {
      setCurrentStepIndex((currentStepIndex) => currentStepIndex - 1);
    }
  };

  const isFinalStep = steps[currentStepIndex].step === steps.length - 1;

  return (
    <PageWrapper
      sx={(theme) => ({
        ...getSafeAreaPaddings({
          top: true,
          bottom: true,
          fallbackBottom: theme.spacing(4),
        }),
        background: "#fff",
      })}
    >
      <Stack p={5} sx={{ minHeight: "100%", overflowY: "auto" }}>
        <Stack direction="row" alignItems="center" position="relative">
          <IconButton iconType="chevron-left" size="medium" variant="outlined" onClick={onBack} />
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <Title variant="h5" component="h1">
              Demo Shift
            </Title>
          </Box>
        </Stack>

        {orientationVideos.length === 0 && (
          <Stack flex={1} alignItems="center">
            <Stack spacing={4}>
              <Title variant="h3" component="h2">
                Something went wrong
              </Title>

              <Text light variant="body1">
                Demo Shift videos not found. Please contact support.
              </Text>
            </Stack>
          </Stack>
        )}
        <>
          {steps[currentStepIndex].stepDetails.type === "intro" && (
            <ClipboardOrientationVideoStartStep
              orientationStatus={orientationStatus}
              orientation={orientation}
              timeToCompleteInMinutes={timeToCompleteInMinutes}
              isStarting={isStarting}
              onStart={async () => {
                const result = await startAssessment({
                  assessmentId,
                  workerId,
                });

                logEvent(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_BEGIN_ASSESSMENT_BUTTON_CLICKED, {
                  assessmentId,
                  workerId,
                });

                const assessmentStatusId = result.data.relationships?.assessmentStatus?.data.id;
                const assessmentStatus = result.included?.find(
                  (item) => item.id === assessmentStatusId
                );

                // If worker failed while starting the assessment, we need to refresh the data so status is updated
                if (assessmentStatus?.attributes?.status === SkillsAssessmentWorkerStatus.FAILED) {
                  await onComplete();
                  return;
                }

                // else let the flow continue
                setCurrentStepIndex((currentStepIndex) => currentStepIndex + 1);
              }}
            />
          )}
          {steps[currentStepIndex].stepDetails.type === "video" && (
            <VideoStep
              videoUrl={steps[currentStepIndex].stepDetails.videoUrl}
              videoPosterUrl={steps[currentStepIndex].stepDetails.videoPosterUrl}
              stepInfo={`Step ${steps[currentStepIndex].step} of ${steps.length - 1}`}
              workerId={workerId}
              assessmentId={assessmentId}
              stepNumber={steps[currentStepIndex].step}
              onContinue={() => {
                setCurrentStepIndex((currentStepIndex) => currentStepIndex + 1);
                logEvent(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_VIDEO_STEP_COMPLETED, {
                  assessmentId,
                  workerId,
                  step: steps[currentStepIndex].step,
                });
              }}
            />
          )}
          {steps[currentStepIndex].stepDetails.type === "recap" && (
            <RecapStep
              recapText={steps[currentStepIndex].stepDetails.recapText}
              confirmationText={steps[currentStepIndex].stepDetails.confirmationText}
              stepInfo={`Step ${steps[currentStepIndex].step} of ${steps.length - 1}`}
              workerId={workerId}
              assessmentId={assessmentId}
              stepNumber={steps[currentStepIndex].step}
              isOrientationBeingSubmitted={isSubmittingAssessment}
              // Disable continue button during final step if we are refreshing orientation
              // We refresh orientation status after submission, this prevents user from pressing continue button
              isOrientationDataLoading={isFinalStep && isRefreshingOrientation}
              onContinue={async () => {
                logEvent(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_RECAP_STEP_COMPLETED, {
                  assessmentId,
                  workerId,
                  step: steps[currentStepIndex].step,
                });

                if (isFinalStep) {
                  await submitAssessment({
                    assessmentId,
                    workerId,
                  });

                  logEvent(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_COMPLETED, {
                    assessmentId,
                    workerId,
                  });

                  await onComplete();
                } else {
                  setCurrentStepIndex((currentStepIndex) => currentStepIndex + 1);
                }
              }}
            />
          )}
          {steps[currentStepIndex].stepDetails.type === "quiz" && (
            <QuizStep
              quiz={steps[currentStepIndex].stepDetails.quiz}
              stepInfo={`Step ${steps[currentStepIndex].step} of ${steps.length - 1}`}
              workerId={workerId}
              assessmentId={assessmentId}
              stepNumber={steps[currentStepIndex].step}
              isOrientationBeingSubmitted={isSubmittingAssessment}
              // Disable continue button during final step if we are refreshing orientation
              // We refresh orientation status after submission, this prevents user from pressing continue button
              isOrientationDataLoading={isFinalStep && isRefreshingOrientation}
              onContinue={async () => {
                if (isFinalStep) {
                  await submitAssessment({
                    assessmentId,
                    workerId,
                  });

                  logEvent(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_COMPLETED, {
                    assessmentId,
                    workerId,
                  });

                  await onComplete();
                } else {
                  setCurrentStepIndex((currentStepIndex) => currentStepIndex + 1);
                }
              }}
            />
          )}
        </>
      </Stack>
    </PageWrapper>
  );
}
/* eslint-enable max-lines */
